import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Folder, File as FileIcon, Upload, FolderPlus, Trash2, Edit3, Download,
  ChevronRight, Save, X, FileArchive, Loader2, Search,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Loading, EmptyState, fmtBytes } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export default function Files() {
  const [sites, setSites] = useState([]);
  const [params, setParams] = useSearchParams();
  const [slug, setSlug] = useState(params.get("site") || "");
  const [path, setPath] = useState("");
  const [listing, setListing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef();
  const { canWrite } = useAuth();

  useEffect(() => {
    api.get("/sites").then((s) => {
      setSites(s);
      if (!slug && s.length) setSlug(s[0].slug);
    });
  }, []);

  const loadDir = () => {
    if (!slug) return;
    api.get(`/files/${slug}`, { params: { path } }).then(setListing).catch(() => setListing({ items: [] }));
  };
  useEffect(() => { loadDir(); setEditing(null); }, [slug, path]);

  const openEntry = (item) => {
    if (item.is_dir) { setPath(item.path); return; }
    if (item.ext === ".zip") return;
    api.get(`/files/${slug}/content`, { params: { path: item.path } })
      .then((r) => { setEditing(item.path); setContent(r.content); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Cannot open"));
  };

  const saveFile = async () => {
    setSaving(true);
    try { await api.put(`/files/${slug}/content`, { path: editing, content }); toast.success("Saved (snapshot created)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("path", path); fd.append("file", file);
    try { await api.post(`/files/${slug}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); loadDir(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const mkdir = async () => {
    const name = prompt("New folder name"); if (!name) return;
    try { await api.post(`/files/${slug}/mkdir`, { path: path ? `${path}/${name}` : name }); loadDir(); } catch { toast.error("Failed"); }
  };
  const rename = async (item) => {
    const name = prompt("Rename to", item.name); if (!name) return;
    const parent = item.path.includes("/") ? item.path.slice(0, item.path.lastIndexOf("/") + 1) : "";
    try { await api.post(`/files/${slug}/rename`, { src: item.path, dst: parent + name }); loadDir(); } catch { toast.error("Failed"); }
  };
  const del = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try { await api.del(`/files/${slug}`, { params: { path: item.path } }); toast.success("Deleted"); loadDir(); } catch { toast.error("Failed"); }
  };
  const extract = async (item) => {
    try { await api.post(`/files/${slug}/extract`, { path: item.path }); toast.success("Extracted"); loadDir(); } catch { toast.error("Failed"); }
  };

  const crumbs = path ? path.split("/") : [];

  return (
    <div data-testid="files-page">
      <PageHeader
        title="File Manager"
        breadcrumb="cannon / files"
        actions={
          <Select value={slug} onValueChange={(v) => { setSlug(v); setPath(""); }}>
            <SelectTrigger className="w-52 bg-zinc-900 border-zinc-800" data-testid="file-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-zinc-800">
              {sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      {!slug ? <EmptyState icon={Folder} title="Select a site" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Browser */}
          <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] overflow-hidden">
            <div className="flex items-center gap-1 border-b border-zinc-800 px-3 py-2 text-xs font-mono text-zinc-400 flex-wrap">
              <button onClick={() => setPath("")} className="hover:text-zinc-100">{slug}</button>
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 text-zinc-700" />
                  <button onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))} className="hover:text-zinc-100">{c}</button>
                </span>
              ))}
              {canWrite && (
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => uploadRef.current?.click()} data-testid="file-upload-btn" className="p-1 hover:text-zinc-100"><Upload className="h-3.5 w-3.5" /></button>
                  <button onClick={mkdir} data-testid="file-mkdir-btn" className="p-1 hover:text-zinc-100"><FolderPlus className="h-3.5 w-3.5" /></button>
                  <input ref={uploadRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                </div>
              )}
            </div>
            <div className="max-h-[65vh] overflow-auto">
              {!listing ? <Loading /> : listing.items.length === 0 ? (
                <div className="text-center text-zinc-600 text-sm py-10 font-mono">empty directory</div>
              ) : listing.items.map((item) => (
                <div key={item.path} data-testid={`file-item-${item.name}`}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-zinc-900/60 group border-b border-zinc-900">
                  <button onClick={() => openEntry(item)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                    {item.is_dir ? <Folder className="h-4 w-4 text-sky-400 shrink-0" /> :
                      item.ext === ".zip" ? <FileArchive className="h-4 w-4 text-amber-400 shrink-0" /> :
                      <FileIcon className="h-4 w-4 text-zinc-500 shrink-0" />}
                    <span className="truncate text-zinc-200">{item.name}</span>
                    {!item.is_dir && <span className="ml-auto font-mono text-xs text-zinc-600 shrink-0">{fmtBytes(item.size)}</span>}
                  </button>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    {item.ext === ".zip" && canWrite && <button onClick={() => extract(item)} title="Extract" className="p-1 text-zinc-500 hover:text-emerald-400"><FileArchive className="h-3.5 w-3.5" /></button>}
                    {!item.is_dir && <a href={`${API}/files/${slug}/download?path=${encodeURIComponent(item.path)}`} className="p-1 text-zinc-500 hover:text-zinc-200"><Download className="h-3.5 w-3.5" /></a>}
                    {canWrite && <button onClick={() => rename(item)} className="p-1 text-zinc-500 hover:text-zinc-200"><Edit3 className="h-3.5 w-3.5" /></button>}
                    {canWrite && <button onClick={() => del(item)} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 border-b border-zinc-800 px-3 py-2 text-xs font-mono text-zinc-400">
              {editing ? <><Edit3 className="h-3.5 w-3.5" /> {editing}</> : <span className="text-zinc-600">no file open · click a text file to edit</span>}
              {editing && (
                <div className="ml-auto flex items-center gap-1">
                  {canWrite && <button onClick={saveFile} disabled={saving} data-testid="save-file-btn" className="flex items-center gap-1 rounded bg-zinc-100 text-zinc-900 px-2 py-1 hover:bg-white disabled:opacity-50">
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save</button>}
                  <button onClick={() => setEditing(null)} className="p-1 hover:text-zinc-100"><X className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            {editing ? (
              <textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid="file-editor" spellCheck={false}
                className="flex-1 min-h-[65vh] w-full resize-none bg-black p-3 text-xs font-mono text-zinc-200 focus:outline-none" />
            ) : (
              <div className="flex-1 min-h-[65vh] flex items-center justify-center text-zinc-700 font-mono text-sm">
                editor · 2MB limit · auto-snapshot on save
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
